import { Alert, Button, Classes, FormGroup, H3, InputGroup, Intent, Switch } from '@blueprintjs/core';
import classNames from 'classnames';
import { LocaleSelector } from 'es/app/views/system/sso-user-templates/components/LocaleSelector';
import { useUnknownUserTemplate } from 'es/app/views/system/sso-user-templates/hooks/useUnknownUserTemplate';
import { SSO_USER_TEMPLATE_FIELD_NAME as FIELD_NAME } from 'es/app/views/system/sso-user-templates/SsoUserTemplates.constants';
import { SSOUserTemplateFormFields } from 'es/app/views/system/sso-user-templates/SsoUserTemplates.types';
import { useI18n } from 'es/core/hooks/useI18n';
import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router';
import styles from './SSOUserTemplateDetails.module.css';
import { useUnknownUserTemplates } from 'es/app/views/system/sso-user-templates/hooks/useUnknownUserTemplates';
import { useMutation } from '@tanstack/react-query';
import { deleteUnknownUserTemplate } from 'es/app/views/system/sso-user-templates/deleteUnknownUserTemplate';
import Guid from 'es/core/types/Guid';
import SystemRouteMap from 'es/app/views/system/RouteMap';

export function SSOUserTemplateDetails() {
    const { id } = useParams();
    const { isFetchingTemplate: isLoading } = useUnknownUserTemplate(id);
    const domain = useFieldProps({ isLoading, field: FIELD_NAME.domain });
    const defaultFirstName = useFieldProps({ isLoading, field: FIELD_NAME.defaultFirstName });
    const defaultLastName = useFieldProps({ isLoading, field: FIELD_NAME.defaultLastName });
    const formsAuthEnabled = useFieldProps({ isLoading, field: FIELD_NAME.formsAuthEnabled });
    const locale = useFieldProps({ isLoading, field: FIELD_NAME.locale });
    return (
        <div className={styles.fieldsWrapper}>
            <FormGroup label={domain.label.text} labelFor={domain.label.htmlFor}>
                <InputGroup {...domain.input} />
            </FormGroup>
            <FormGroup label={defaultFirstName.label.text} labelFor={defaultFirstName.label.htmlFor}>
                <InputGroup {...defaultFirstName.input} />
            </FormGroup>
            <FormGroup label={defaultLastName.label.text} labelFor={defaultLastName.label.htmlFor}>
                <InputGroup {...defaultLastName.input} />
            </FormGroup>
            <FormGroup label={locale.label.text} labelFor={locale.label.htmlFor}>
                <LocaleSelector id={locale.input.id} value={locale.input.value} />
            </FormGroup>
            <FormGroup label={formsAuthEnabled.label.text} labelFor={formsAuthEnabled.label.htmlFor}>
                <Switch
                    {...formsAuthEnabled.input}
                    checked={formsAuthEnabled.input.value}
                    value={formsAuthEnabled.input.value ? 'true' : 'false'}
                />
            </FormGroup>
            <DeleteTemplate />
        </div>
    );
}

function DeleteTemplate() {
    const { l10n } = useI18n('app/views/system/sso-user-templates');
    const { values } = useFormikContext<SSOUserTemplateFormFields>();
    const { id } = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const { isFetchingTemplate: isLoading } = useUnknownUserTemplate(id);
    const { invalidateTemplates } = useUnknownUserTemplates();
    const { mutateAsync: handleDeleteUnknownUserTemplate, isPending: isDeletingTemplate } = useMutation({
        mutationFn: deleteUnknownUserTemplate,
    });
    const navigate = useNavigate();
    const onDeleteTemplate = useCallback(
        async (id: Guid) => {
            const result = await handleDeleteUnknownUserTemplate(id);
            if (!result) return;
            navigate(generatePath(SystemRouteMap.ssoUserTemplates.search()));
            invalidateTemplates();
        },
        [handleDeleteUnknownUserTemplate, invalidateTemplates, navigate]
    );
    if (!id) return null;
    return (
        <>
            <Button intent={Intent.DANGER} onClick={() => setIsOpen(true)} disabled={isLoading}>
                {l10n('actions.deleteTemplate')}
            </Button>
            <Alert
                confirmButtonText={l10n('actions.deleteTemplate')}
                cancelButtonText={l10n('actions.cancel')}
                isOpen={isOpen}
                canEscapeKeyCancel={!isDeletingTemplate ? true : false}
                canOutsideClickCancel={!isDeletingTemplate ? true : false}
                onCancel={() => !isDeletingTemplate && setIsOpen(false)}
                intent={Intent.DANGER}
                loading={isDeletingTemplate}
                onConfirm={() => onDeleteTemplate(id)}
            >
                <H3>{l10n('actions.deleteTemplate')}</H3>
                <p>{l10n('deleteTemplate.confirmationMessage', { domain: values.domain })}</p>
            </Alert>
        </>
    );
}

function useFieldProps<K extends (typeof FIELD_NAME)[keyof typeof FIELD_NAME]>({
    isLoading,
    field,
}: {
    isLoading?: boolean;
    field: K;
}) {
    const { l10n } = useI18n('app/views/system/sso-user-templates', 'fields');
    const { handleChange, values } = useFormikContext<SSOUserTemplateFormFields>();
    const className = classNames({
        [Classes.SKELETON]: isLoading,
    });
    return useMemo(() => {
        const id = `unknown-user-field-${field}`;
        return {
            label: {
                htmlFor: id,
                text: l10n(`${field}.label`),
            },
            input: {
                name: field,
                id,
                onChange: handleChange,
                value: values[field],
                className,
            },
        };
    }, [className, field, handleChange, l10n, values]);
}
